import React, { useState, useEffect, useCallback } from "react";
import classNames from "classnames";
import {
  PencilSquareIcon,
  XMarkIcon,
  CheckIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import {
  listExpeditions,
  getExpeditionShow,
  changeExpeditionStatus,
} from "../../../services/crm/clementino-service";
import {
  ClementinoExpedition,
  ClementinoTrackingEvent,
  normalizeStatus,
} from "../../../models/crm/clementino/clementino-types";
import { toFloat, kg } from "./envois-shared";

interface NotificationItem {
  channel: string;
  at: string;
  text: string;
}

interface TrackingEvent {
  at: string;
  step: number;
  label: string;
  loc: string;
  detail: string;
  src: "Transporteur" | "Agent" | "Système" | "Incident";
  pub: boolean;
  notified: boolean;
}

interface Shipment {
  id: string;
  numericId: number;
  code: string;
  awb: string;
  sender: string;
  phone: string;
  receiver: string;
  receiverPhone: string;
  from: string;
  to: string;
  mode: "Aérien" | "Maritime" | "Express" | "Urbain";
  departInfo: string;
  eta: string;
  colis: number;
  poids: string;
  nature: string;
  pay: "Payé" | "Non payé" | "Partiel" | "Remboursé";
  status: "Attente dépôt" | "Réceptionné" | "En transit" | "Dédouanement" | "Livré" | "Annulé";
  alert: string;
  events: TrackingEvent[];
  notifs: NotificationItem[];
  raw?: ClementinoExpedition;
}

const STEPS_INT = ["Enregistré", "Réceptionné", "Départ", "Arrivé à destination", "Dédouanement", "Livré"];
const STEPS_URB = ["Enregistré", "Coursier affecté", "Ramassé", "En livraison", "Livré"];

const LOGI: Record<Shipment["status"], { bg: string; fg: string; dot: string }> = {
  "Attente dépôt": { bg: "#fff7ed", fg: "#c2410c", dot: "#fb923c" },
  "Réceptionné": { bg: "#ede9fe", fg: "#5b21b6", dot: "#7c3aed" },
  "En transit": { bg: "#e0f2fe", fg: "#0369a1", dot: "#0ea5e9" },
  "Dédouanement": { bg: "#fef3c7", fg: "#b45309", dot: "#f59e0b" },
  "Livré": { bg: "#d1fae5", fg: "#047857", dot: "#10b981" },
  "Annulé": { bg: "#f1f5f9", fg: "#64748b", dot: "#94a3b8" },
};

const PAY: Record<Shipment["pay"], { bg: string; fg: string }> = {
  "Payé": { bg: "#d1fae5", fg: "#047857" },
  "Non payé": { bg: "#fee2e2", fg: "#b91c1c" },
  "Partiel": { bg: "#fef3c7", fg: "#b45309" },
  "Remboursé": { bg: "#e5e7eb", fg: "#4b5563" },
};

const MODE_STYLE: Record<Shipment["mode"], { bg: string; fg: string }> = {
  "Aérien": { bg: "#e0f2fe", fg: "#0369a1" },
  "Maritime": { bg: "#e0e7ff", fg: "#3730a3" },
  "Express": { bg: "#ffe4e6", fg: "#be123c" },
  "Urbain": { bg: "#dcfce7", fg: "#15803d" },
};

const SRC_STYLE: Record<TrackingEvent["src"], { bg: string; fg: string; dot: string }> = {
  "Transporteur": { bg: "#e0f2fe", fg: "#0369a1", dot: "#0ea5e9" },
  "Agent": { bg: "#ede9fe", fg: "#5b21b6", dot: "#7c3aed" },
  "Système": { bg: "#f1f5f9", fg: "#475569", dot: "#94a3b8" },
  "Incident": { bg: "#fee2e2", fg: "#b91c1c", dot: "#ef4444" },
};

const E = (
  at: string,
  step: number,
  label: string,
  loc: string,
  detail: string,
  src: TrackingEvent["src"],
  pub: boolean = true,
  notified: boolean = false
): TrackingEvent => ({ at, step, label, loc, detail, src, pub, notified });

const STATUSES: Shipment["status"][] = [
  "Attente dépôt",
  "Réceptionné",
  "En transit",
  "Dédouanement",
  "Livré",
  "Annulé",
];

const clementinoStatusToLocal = (st?: any): Shipment["status"] => {
  const s = normalizeStatus(st).toUpperCase();
  if (!s) return "Attente dépôt";
  if (s.includes("ATTENTE") || s === "CREE") return "Attente dépôt";
  if (s.includes("RECEP") || s.includes("DEPOT")) return "Réceptionné";
  if (s.includes("TRANSIT") || s.includes("EXPED") || s.includes("COURS")) return "En transit";
  if (s.includes("DOUAN")) return "Dédouanement";
  if (s.includes("LIVR") || s.includes("TERMIN")) return "Livré";
  if (s.includes("ANNUL") || s.includes("REFUS")) return "Annulé";
  return "En transit";
};

const localStatusToClementino = (st: string): string => {
  switch (st) {
    case "Attente dépôt":
      return "EN_ATTENTE_DEPOT";
    case "Réceptionné":
      return "RECEPTIONNE";
    case "En transit":
      return "EN_TRANSIT";
    case "Dédouanement":
      return "DEDOUANEMENT";
    case "Livré":
      return "LIVRE";
    case "Annulé":
      return "ANNULE";
    default:
      return "EN_TRANSIT";
  }
};

const stepsFor = (s: Shipment) => (s.mode === "Urbain" ? STEPS_URB : STEPS_INT);

const nowStr = () => {
  const d = new Date();
  const p = (n: number) => ("0" + n).slice(-2);
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const clementinoEventToTrackingEvent = (
  ev: ClementinoTrackingEvent,
  mode: Shipment["mode"]
): TrackingEvent => {
  const d = ev.date ? new Date(ev.date) : new Date();
  const pad = (n: number) => ("0" + n).slice(-2);
  const at = isNaN(d.getTime())
    ? String(ev.date || nowStr())
    : `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;

  const stLocal = clementinoStatusToLocal(ev.status);
  const steps = mode === "Urbain" ? STEPS_URB : STEPS_INT;
  let stepIdx = 0;
  if (stLocal === "Attente dépôt") stepIdx = 0;
  else if (stLocal === "Réceptionné") stepIdx = 1;
  else if (stLocal === "En transit") stepIdx = 2;
  else if (stLocal === "Dédouanement") stepIdx = Math.min(4, steps.length - 2);
  else if (stLocal === "Livré") stepIdx = steps.length - 1;

  const stStr = normalizeStatus(ev.status);
  const isIncident = stStr.toUpperCase().includes("INCIDENT");

  return {
    at,
    step: stepIdx,
    label: stStr || "Statut actualisé",
    loc: ev.localisationGps || "",
    detail: ev.commentaire || (ev.retirerPar ? `Retiré par ${ev.retirerPar}` : ""),
    src: isIncident ? "Incident" : ev.agent ? "Agent" : "Transporteur",
    pub: true,
    notified: false,
  };
};

const clementinoToExpeditionShipment = (e: ClementinoExpedition): Shipment => {
  const isUrbain = e.type === "urbain" || !!e.coursier;
  const isExpress = (e.type || "").toLowerCase().includes("express");
  const isMaritime = (e.type || "").toLowerCase().includes("maritime");
  const mode: Shipment["mode"] = isUrbain ? "Urbain" : isExpress ? "Express" : isMaritime ? "Maritime" : "Aérien";

  const colisList: any[] = Array.isArray(e.colis)
    ? e.colis
    : e.colis && typeof e.colis === "object"
    ? [e.colis]
    : [];

  const numColis = colisList.length > 0
    ? colisList.reduce((sum, c) => sum + (Math.round(toFloat(c.nombre, 1)) || 1), 0)
    : (Math.round(toFloat((e as any).nbTotalColis, 1)) || 1);

  const totalWeight = colisList.reduce((sum, c) => sum + toFloat(c.poidsColis ?? c.poids, 0), 0);

  const nature = colisList[0]
    ? (colisList[0].description || String(colisList[0].natureColis || "Colis standard"))
    : String((e as any).natureColis || "Colis standard");

  const fromCity = typeof e.villeExpedition === "object" && e.villeExpedition !== null
    ? ((e.villeExpedition as any).nom || (e.villeExpedition as any).name || "DOUALA")
    : String(e.villeExpedition || "DOUALA");

  const toCity = typeof e.villeDestination === "object" && e.villeDestination !== null
    ? ((e.villeDestination as any).nom || (e.villeDestination as any).name || "PARIS")
    : String(e.villeDestination || "PARIS");

  const dateCre = e.dateCreation || e.createdAt;
  const departInfo = dateCre
    ? `Enregistré le ${new Date(dateCre).toLocaleDateString("fr-FR")}`
    : `Dossier #${e.id}`;

  const rawStatus = (e as any).status ?? (e as any).statut;
  const mappedStatus = clementinoStatusToLocal(rawStatus);

  const events: TrackingEvent[] = Array.isArray(e.trackingEvents) && e.trackingEvents.length > 0
    ? e.trackingEvents.map((ev) => clementinoEventToTrackingEvent(ev, mode))
    : [
        E(
          dateCre
            ? new Date(dateCre).toLocaleDateString("fr-FR") + " 09:00"
            : nowStr(),
          0,
          "Expédition enregistrée",
          String(fromCity),
          `Enregistrement Clementino #${e.numLt || (e as any).numLettreTransport || e.id}`,
          "Système"
        ),
      ];

  const montantVal = toFloat(e.montant, 0);
  const pay: Shipment["pay"] = e.modePaiement
    ? "Payé"
    : montantVal > 0
    ? "Non payé"
    : "Payé";

  return {
    id: String(e.id),
    numericId: e.id,
    code: e.numLt || (e as any).numLettreTransport || (e as any).numExpedition || (e as any).numeroExpedition || `EXP-${e.id}`,
    awb: "",
    sender: e.contactExpediteur || "Expéditeur inconnu",
    phone: e.telExpediteur || "—",
    receiver: e.contactDestinataire || "Destinataire inconnu",
    receiverPhone: e.telDestinataire || "—",
    from: String(fromCity).toUpperCase(),
    to: String(toCity).toUpperCase(),
    mode,
    departInfo,
    eta: e.dateLivraisonSouhaite || "—",
    colis: numColis,
    poids: totalWeight > 0 ? kg(totalWeight) : "—",
    nature,
    pay,
    status: mappedStatus,
    alert: "",
    events,
    notifs: [],
    raw: e,
  };
};

export default function TrackingColis() {
  const [ships, setShips] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selId, setSelId] = useState<string | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<Shipment | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Filters
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState<string>("Tous");
  const [fDateDebut, setFDateDebut] = useState<string>("");
  const [fDateFin, setFDateFin] = useState<string>("");
  const [fType, setFType] = useState<string>("Tous");

  const [showInternal, setShowInternal] = useState(true);
  const [lastSync, setLastSync] = useState(nowStr().slice(11));
  const [isFullScreenPage, setIsFullScreenPage] = useState(false);
  const [isFullScreenDetail, setIsFullScreenDetail] = useState(false);
  const [isLeftListCollapsed, setIsLeftListCollapsed] = useState(false);
  const [toast, setToast] = useState("");

  // Event form
  const [formKind, setFormKind] = useState("");
  const [formLoc, setFormLoc] = useState("");
  const [formNote, setFormNote] = useState("");
  const [formPub, setFormPub] = useState(true);
  const [formNotify, setFormNotify] = useState(true);
  const [submittingEvent, setSubmittingEvent] = useState(false);

  // Edit Modal State
  const [editingShipment, setEditingShipment] = useState<Shipment | null>(null);
  const [editFormData, setEditFormData] = useState({
    code: "",
    awb: "",
    sender: "",
    phone: "",
    receiver: "",
    receiverPhone: "",
    from: "",
    to: "",
    mode: "Aérien" as Shipment["mode"],
    status: "Attente dépôt" as Shipment["status"],
    pay: "Payé" as Shipment["pay"],
    colis: 1,
    poids: "",
    nature: "",
    eta: "",
    departInfo: "",
    alert: "",
  });

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3500);
  };

  // Charger la liste des expéditions
  const fetchShipmentsList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filtres: Record<string, any> = {};
      if (fStatus !== "Tous") {
        filtres.status = localStatusToClementino(fStatus);
      }
      if (fDateDebut) filtres.date_debut = fDateDebut;
      if (fDateFin) filtres.date_fin = fDateFin;
      if (fType !== "Tous") filtres.type = fType;
      if (q.trim()) filtres.search = q.trim();

      const entities = await listExpeditions(filtres);
      const adapted = entities.map(clementinoToExpeditionShipment);
      setShips(adapted);

      if (adapted.length > 0) {
        // Conserver la sélection existante si elle est toujours dans la liste
        setSelId((prev) => {
          if (prev && adapted.some((s) => s.id === prev)) return prev;
          return adapted[0].id;
        });
      } else {
        setSelId(null);
        setSelectedDetail(null);
      }
      setLastSync(nowStr().slice(11));
    } catch (err: any) {
      const errMsg =
        err?.message ||
        "Impossible de charger les expéditions depuis Clementino. Vérifiez votre clé Gateway API.";
      setError(errMsg);
      setShips([]);
      setSelId(null);
      setSelectedDetail(null);
    } finally {
      setLoading(false);
    }
  }, [fStatus, fDateDebut, fDateFin, fType, q]);

  // Chargement initial et lors des filtres
  useEffect(() => {
    fetchShipmentsList();
  }, [fetchShipmentsList]);

  // Charger le détail et tracking complet (Clementino/Expeditions/show)
  const fetchShipmentDetail = useCallback(async (codeOrLt: string) => {
    setLoadingDetail(true);
    setDetailError(null);
    try {
      const exp = await getExpeditionShow(codeOrLt);
      if (exp) {
        const fullShipment = clementinoToExpeditionShipment(exp);
        setSelectedDetail(fullShipment);
      }
    } catch (err: any) {
      setDetailError(
        "Impossible de récupérer le suivi en direct pour cette expédition: " +
          (err?.message || "Erreur serveur")
      );
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  // Déclencher le show lorsqu'une expédition est sélectionnée
  useEffect(() => {
    if (!selId) {
      setSelectedDetail(null);
      return;
    }
    const current = ships.find((x) => x.id === selId);
    if (current) {
      fetchShipmentDetail(current.code);
    }
  }, [selId, ships, fetchShipmentDetail]);

  const curStep = (s: Shipment) => s.events.reduce((m, e) => Math.max(m, e.step), 0);

  const selectedShipment =
    selectedDetail || ships.find((x) => x.id === selId) || null;

  const handleSelectShipment = (id: string) => {
    setSelId(id);
    setFormKind("");
    setFormLoc("");
    setFormNote("");
  };

  const handleOpenEdit = (e: React.MouseEvent, s: Shipment) => {
    e.stopPropagation();
    setEditingShipment(s);
    setEditFormData({
      code: s.code,
      awb: s.awb,
      sender: s.sender,
      phone: s.phone,
      receiver: s.receiver,
      receiverPhone: s.receiverPhone,
      from: s.from,
      to: s.to,
      mode: s.mode,
      status: s.status,
      pay: s.pay,
      colis: s.colis,
      poids: s.poids,
      nature: s.nature,
      eta: s.eta,
      departInfo: s.departInfo,
      alert: s.alert,
    });
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingShipment) return;

    try {
      const apiStatus = localStatusToClementino(editFormData.status);
      await changeExpeditionStatus(editingShipment.numericId, {
        status: apiStatus,
        commentaire: `Modification manuelle: statut mis à jour en ${editFormData.status}`,
      });
      flash(`Expédition ${editFormData.code} mise à jour avec succès dans Clementino`);
      setEditingShipment(null);
      await fetchShipmentDetail(editingShipment.code);
      await fetchShipmentsList();
    } catch (err: any) {
      flash("Erreur lors de la mise à jour: " + (err.message || "Erreur serveur"));
    }
  };

  const handleAddEvent = async () => {
    if (!selectedShipment) return;
    const steps = stepsFor(selectedShipment);
    const kind = formKind || steps[Math.min(curStep(selectedShipment) + 1, steps.length - 1)];
    const isInc = kind === "Incident";
    const apiStatus = isInc ? "INCIDENT" : localStatusToClementino(kind);

    setSubmittingEvent(true);
    try {
      await changeExpeditionStatus(selectedShipment.numericId, {
        status: apiStatus,
        commentaire: formNote || (isInc ? "Incident signalé" : kind),
        localisationGps: formLoc || undefined,
      });

      flash(
        formNotify
          ? "Événement enregistré avec succès dans Clementino · Client notifié"
          : "Événement enregistré avec succès dans Clementino"
      );

      setFormKind("");
      setFormLoc("");
      setFormNote("");

      // Rafraîchir les détails complets depuis l'API Clementino
      await fetchShipmentDetail(selectedShipment.code);
      await fetchShipmentsList();
    } catch (err: any) {
      flash("Erreur lors de l'enregistrement de l'événement: " + (err?.message || "Erreur serveur"));
    } finally {
      setSubmittingEvent(false);
    }
  };

  const handleResolveAlert = async () => {
    if (!selectedShipment) return;
    try {
      await changeExpeditionStatus(selectedShipment.numericId, {
        status: localStatusToClementino(selectedShipment.status),
        commentaire: "Alerte logistique résolue",
      });
      flash("Alerte marquée comme résolue sur Clementino");
      await fetchShipmentDetail(selectedShipment.code);
    } catch (err: any) {
      flash("Erreur lors de la résolution de l'alerte: " + (err?.message || "Erreur serveur"));
    }
  };

  const handleCopyLink = () => {
    if (!selectedShipment) return;
    try {
      navigator.clipboard.writeText(`https://suivi.mopocrm.com/t/${selectedShipment.code}`);
      flash("Lien de suivi copié dans le presse-papiers");
    } catch (e) {
      flash("Lien copié");
    }
  };

  const handleSendLink = () => {
    if (!selectedShipment) return;
    flash(`Lien envoyé par WhatsApp à ${selectedShipment.receiver}`);
  };

  // Compteurs
  const counts: Record<string, number> = {};
  ships.forEach((s) => {
    counts[s.status] = (counts[s.status] || 0) + 1;
  });

  const activeCount = ships.filter((x) => x.status !== "Livré" && x.status !== "Annulé").length;
  const alertCount = ships.filter((x) => !!x.alert).length;

  const currentShipment = selectedShipment;
  const currentSteps = currentShipment ? stepsFor(currentShipment) : [];
  const currentCurStep = currentShipment ? curStep(currentShipment) : 0;
  const currentCancelled = currentShipment?.status === "Annulé";

  const firstAtMap: Record<number, string> = {};
  if (currentShipment) {
    currentShipment.events.forEach((e) => {
      if (!(e.step in firstAtMap)) firstAtMap[e.step] = e.at;
    });
  }

  const visibleEvents = currentShipment
    ? currentShipment.events.filter((e) => showInternal || e.pub).slice().reverse()
    : [];

  const kindOptions = currentShipment ? currentSteps.concat(["Incident"]) : [];

  return (
    <div
      className={classNames(
        "h-full flex flex-col bg-[#faf7ff] text-[#1f1b2e] overflow-hidden transition-all",
        isFullScreenPage ? "fixed inset-0 z-50 p-6 bg-[#faf7ff]" : "p-6 lg:p-7"
      )}
    >
      {/* Top Header - Always pinned at top */}
      <div className="flex items-start justify-between gap-6 flex-wrap mb-4 shrink-0">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-[#1f1b2e]">
              Tracking & Suivi des Colis
            </h1>
            <button
              type="button"
              onClick={() => setIsFullScreenPage(!isFullScreenPage)}
              title={isFullScreenPage ? "Quitter le plein écran" : "Plein écran sur toute la page"}
              className="p-1.5 rounded-lg border border-purple-200 bg-white hover:bg-purple-50 text-purple-800 text-xs font-semibold flex items-center gap-1 shadow-2xs cursor-pointer"
            >
              {isFullScreenPage ? (
                <>
                  <ArrowsPointingInIcon className="w-3.5 h-3.5" />
                  <span>Réduire</span>
                </>
              ) : (
                <>
                  <ArrowsPointingOutIcon className="w-3.5 h-3.5" />
                  <span>Plein écran</span>
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-[#6b6480] mt-1 font-medium">
            {activeCount} colis actif{activeCount > 1 ? "s" : ""} · {alertCount} alerte{alertCount > 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs text-[#8b81a8]">Dernière sync. {lastSync}</span>
          <button
            type="button"
            onClick={fetchShipmentsList}
            disabled={loading}
            className="h-9 px-3.5 border border-blue-200 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 rounded-lg text-xs font-semibold text-blue-700 transition-colors shadow-xs cursor-pointer flex items-center gap-2"
          >
            <ArrowPathIcon className={classNames("w-3.5 h-3.5", loading && "animate-spin")} />
            <span>Actualiser Clementino</span>
          </button>
        </div>
      </div>

      {/* Filter and Multi-criteria Search Bar */}
      <div className="flex items-center gap-3 flex-wrap mb-3.5 shrink-0 bg-white p-3 rounded-xl border border-purple-100 shadow-xs">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Code expédition, n° AWB, client, téléphone…"
            className="w-full h-9 pl-9 pr-3 border border-purple-200/90 rounded-lg bg-gray-50/50 text-xs text-[#1f1b2e] focus:outline-none focus:ring-2 focus:ring-purple-400 focus:bg-white"
          />
        </div>

        {/* Date Filters */}
        <div className="flex items-center gap-2">
          <CalendarDaysIcon className="w-4 h-4 text-gray-400" />
          <input
            type="date"
            value={fDateDebut}
            onChange={(e) => setFDateDebut(e.target.value)}
            title="Date de début"
            className="h-9 px-2.5 border border-purple-200 rounded-lg text-xs bg-white text-[#1f1b2e]"
          />
          <span className="text-xs text-gray-400">à</span>
          <input
            type="date"
            value={fDateFin}
            onChange={(e) => setFDateFin(e.target.value)}
            title="Date de fin"
            className="h-9 px-2.5 border border-purple-200 rounded-lg text-xs bg-white text-[#1f1b2e]"
          />
        </div>

        {/* Type Filter */}
        <select
          value={fType}
          onChange={(e) => setFType(e.target.value)}
          className="h-9 px-3 border border-purple-200 rounded-lg text-xs bg-white text-[#1f1b2e]"
        >
          <option value="Tous">Tous les types</option>
          <option value="intex">International (Intex)</option>
          <option value="urbain">Livraison Urbaine</option>
          <option value="national">National / Fret</option>
        </select>

        {/* Status Badges */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            type="button"
            onClick={() => setFStatus("Tous")}
            className={classNames(
              "flex items-center gap-1.5 h-8 px-2.5 rounded-full text-xs font-semibold transition-all cursor-pointer",
              fStatus === "Tous"
                ? "bg-[#1f1b2e] text-white border border-[#1f1b2e]"
                : "bg-white text-[#3f3a52] border border-[#e6e0f5] hover:bg-purple-50"
            )}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-purple-600" />
            Tous <span className="opacity-70 text-[11px]">{ships.length}</span>
          </button>
          {STATUSES.map((st) => {
            const active = fStatus === st;
            return (
              <button
                key={st}
                type="button"
                onClick={() => setFStatus(st)}
                className={classNames(
                  "flex items-center gap-1.5 h-8 px-2.5 rounded-full text-xs font-semibold transition-all cursor-pointer",
                  active
                    ? "bg-[#1f1b2e] text-white border border-[#1f1b2e]"
                    : "bg-white text-[#3f3a52] border border-[#e6e0f5] hover:bg-purple-50"
                )}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: LOGI[st].dot }}
                />
                {st} <span className="opacity-70 text-[11px]">{counts[st] || 0}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Global Error Banner */}
      {error && (
        <div className="mb-4 p-3.5 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <ExclamationTriangleIcon className="w-5 h-5 text-rose-600 shrink-0" />
            <span>{error}</span>
          </div>
          <button
            type="button"
            onClick={fetchShipmentsList}
            className="px-3 py-1 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-lg text-xs"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Main Container: Left List + Right Detail */}
      <div className="flex-1 min-h-0 flex gap-4 items-stretch overflow-hidden relative">
        {/* Left List Container */}
        {!isLeftListCollapsed ? (
          <div className="w-[330px] shrink-0 bg-white border border-[#ece7f8] rounded-xl overflow-hidden shadow-xs flex flex-col transition-all">
            <div className="px-3.5 py-2.5 text-[11px] font-bold tracking-wider uppercase text-[#8b81a8] border-b border-[#f1ecfe] flex items-center justify-between bg-white shrink-0">
              <div className="flex items-center gap-1.5">
                <span>{ships.length} colis</span>
                {loading && <ArrowPathIcon className="w-3 h-3 text-purple-600 animate-spin" />}
              </div>
              <button
                type="button"
                onClick={() => setIsLeftListCollapsed(true)}
                title="Masquer la colonne des colis"
                className="px-2 py-0.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-900 cursor-pointer text-[11px] font-medium flex items-center gap-1"
              >
                <span>Replier</span>
                <ChevronLeftIcon className="w-3 h-3" />
              </button>
            </div>

            {/* Scrollable shipments list */}
            <div className="flex-1 overflow-y-auto tracking-scrollbar divide-y divide-[#f4f1fb] p-1.5">
              {loading && ships.length === 0 && (
                <div className="p-8 text-center text-xs text-purple-700 space-y-2">
                  <ArrowPathIcon className="w-6 h-6 animate-spin mx-auto text-purple-600" />
                  <p>Chargement des expéditions Clementino...</p>
                </div>
              )}

              {!loading && ships.length === 0 && !error && (
                <div className="p-8 text-xs text-gray-400 text-center">
                  Aucune expédition trouvée pour ces critères.
                </div>
              )}

              {ships.map((s) => {
                const L = LOGI[s.status];
                const steps = stepsFor(s);
                const cur = curStep(s);
                const last = s.events[s.events.length - 1];
                const done = s.status === "Livré";
                const late = !!s.alert;
                const isSelected = s.id === selId;
                const pct =
                  s.status === "Annulé"
                    ? "100%"
                    : `${Math.round((cur / (steps.length - 1)) * 100)}%`;

                return (
                  <div
                    key={s.id}
                    onClick={() => handleSelectShipment(s.id)}
                    className={classNames(
                      "p-3 rounded-lg cursor-pointer transition-all border group relative mb-1.5",
                      isSelected
                        ? "bg-purple-50/70 border-purple-600 ring-2 ring-purple-400/30 shadow-xs"
                        : "bg-white border-gray-100 hover:border-purple-200 hover:bg-[#faf7ff]"
                    )}
                  >
                    <div className="flex justify-between items-center gap-2">
                      <span className="font-mono text-xs font-semibold text-emerald-800">
                        {s.code}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <span
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-bold whitespace-nowrap"
                          style={{ backgroundColor: L.bg, color: L.fg }}
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: L.dot }}
                          />
                          {s.status}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => handleOpenEdit(e, s)}
                          title="Modifier cette expédition"
                          className="p-1 rounded-md text-gray-400 hover:text-purple-700 hover:bg-purple-100 transition-colors cursor-pointer"
                        >
                          <PencilSquareIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="text-xs font-semibold text-[#241f36] mt-1.5 flex items-center justify-between">
                      <span>{s.receiver}</span>
                      <span className="text-[10px] text-gray-400 font-normal">{s.poids}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 text-[11px] text-[#6b6480]">
                      <span>
                        {s.from} → {s.to}
                      </span>
                      <span className="text-[#cbc4dc]">·</span>
                      <span>{s.mode}</span>
                    </div>
                    {/* Progress line */}
                    <div className="h-1 bg-[#f1ecfe] rounded-full mt-2 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{ width: pct, backgroundColor: L.dot }}
                      />
                    </div>
                    <div className="flex justify-between gap-2 mt-1.5 text-[11px]">
                      <span className="text-[#9b93b3] truncate max-w-[160px]">
                        {last ? `${last.at.slice(0, 5)} · ${last.label}` : "Initié"}
                      </span>
                      <span
                        className={classNames(
                          "font-semibold whitespace-nowrap",
                          late
                            ? "text-amber-700"
                            : done
                            ? "text-emerald-700"
                            : "text-[#6b6480]"
                        )}
                      >
                        {done ? "Livré" : s.status === "Annulé" ? "—" : `ETA ${s.eta}`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="shrink-0 flex items-start">
            <button
              type="button"
              onClick={() => setIsLeftListCollapsed(false)}
              title="Afficher la liste des colis"
              className="py-3 px-2 bg-white border border-[#ece7f8] rounded-xl hover:bg-purple-50 text-purple-900 text-xs font-bold shadow-xs cursor-pointer flex flex-col items-center gap-2"
            >
              <ChevronRightIcon className="w-4 h-4 text-purple-700" />
              <span className="text-[10px] [writing-mode:vertical-lr] rotate-180 uppercase tracking-widest text-[#6b6480]">
                Colis ({ships.length})
              </span>
            </button>
          </div>
        )}

        {/* Right Detail Pane */}
        {currentShipment ? (
          <div
            className={classNames(
              "flex-1 min-w-0 h-full overflow-y-auto tracking-scrollbar pr-1 flex flex-col gap-3.5 transition-all",
              isFullScreenDetail ? "fixed inset-0 z-50 p-6 bg-[#faf7ff]" : ""
            )}
          >
            {/* Detail Error Banner if show failed */}
            {detailError && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-xs flex items-center justify-between">
                <span>{detailError}</span>
                <button
                  type="button"
                  onClick={() => fetchShipmentDetail(currentShipment.code)}
                  className="font-bold underline ml-2"
                >
                  Réessayer
                </button>
              </div>
            )}

            {/* Top Details Card */}
            <div className="bg-white border border-[#ece7f8] rounded-xl p-5 shadow-xs shrink-0">
              <div className="flex justify-between gap-4 flex-wrap items-start">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2.5 py-1 bg-emerald-100 rounded-md font-mono text-xs font-semibold text-emerald-800">
                      {currentShipment.code}
                    </span>
                    <span
                      className="px-2.5 py-1 rounded-md font-mono text-xs"
                      style={{
                        backgroundColor: currentShipment.awb ? "#e0f2fe" : "#f1f5f9",
                        color: currentShipment.awb ? "#0369a1" : "#94a3b8",
                      }}
                    >
                      {currentShipment.awb || "Suivi Clementino"}
                    </span>
                    <span
                      className="px-2 py-0.5 rounded text-[11px] font-bold"
                      style={{
                        backgroundColor: MODE_STYLE[currentShipment.mode].bg,
                        color: MODE_STYLE[currentShipment.mode].fg,
                      }}
                    >
                      {currentShipment.mode}
                    </span>
                    {loadingDetail && (
                      <span className="text-[11px] text-purple-600 flex items-center gap-1 font-medium">
                        <ArrowPathIcon className="w-3 h-3 animate-spin" />
                        Synchronisation des jalons...
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => handleOpenEdit(e, currentShipment)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-md border border-purple-200 transition-colors cursor-pointer"
                    >
                      <PencilSquareIcon className="w-3.5 h-3.5" />
                      Modifier statut
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsFullScreenDetail(!isFullScreenDetail)}
                      title={
                        isFullScreenDetail
                          ? "Quitter le focus plein écran"
                          : "Zoom plein écran sur cette expédition"
                      }
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md border border-indigo-200 transition-colors cursor-pointer"
                    >
                      {isFullScreenDetail ? (
                        <>
                          <ArrowsPointingInIcon className="w-3.5 h-3.5" />
                          <span>Réduire</span>
                        </>
                      ) : (
                        <>
                          <ArrowsPointingOutIcon className="w-3.5 h-3.5" />
                          <span>Plein écran</span>
                        </>
                      )}
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <span className="text-base font-bold text-[#241f36]">
                      {currentShipment.from}
                    </span>
                    <span className="text-[#b6afc9] text-sm">→</span>
                    <span className="text-base font-bold text-[#241f36]">
                      {currentShipment.to}
                    </span>
                    <span className="text-xs text-[#6b6480] ml-1">
                      {currentShipment.departInfo}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold"
                    style={{
                      backgroundColor: LOGI[currentShipment.status].bg,
                      color: LOGI[currentShipment.status].fg,
                    }}
                  >
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: LOGI[currentShipment.status].dot }}
                    />
                    {currentShipment.status}
                  </span>
                  <div className="text-xs text-[#6b6480] mt-1.5">
                    {currentShipment.status === "Livré" ? "Statut" : "Arrivée estimée"}{" "}
                    <strong className="text-[#241f36]">
                      {currentShipment.status === "Livré" ? "Colis livré" : currentShipment.eta}
                    </strong>
                  </div>
                </div>
              </div>

              {/* Alert banner if any */}
              {currentShipment.alert && (
                <div className="flex items-center gap-2.5 mt-3.5 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
                  <span className="w-2 h-2 rounded-xs bg-amber-500 shrink-0" />
                  <span className="flex-1 font-medium">{currentShipment.alert}</span>
                  <button
                    type="button"
                    onClick={handleResolveAlert}
                    className="h-7 px-2.5 border border-amber-300 bg-white rounded-md text-[11px] font-bold text-amber-800 hover:bg-amber-100 transition-colors cursor-pointer"
                  >
                    Marquer résolu
                  </button>
                </div>
              )}

              {/* Visual Multi-step Progression */}
              <div
                className="grid gap-2 mt-5 pt-4 border-t border-gray-100"
                style={{
                  gridTemplateColumns: `repeat(${currentSteps.length}, minmax(0, 1fr))`,
                }}
              >
                {currentSteps.map((label, i) => {
                  const reached = i <= currentCurStep && !currentCancelled;
                  const isCur = i === currentCurStep && !currentCancelled;
                  const col = isCur && currentShipment.alert ? "#f59e0b" : "#6d28d9";
                  const dotBg = reached ? col : "#fff";
                  const ringColor = reached ? (isCur ? "#ddd0fb" : col) : "#e6e0f5";
                  const lineBg = i < currentCurStep && !currentCancelled ? "#6d28d9" : "#ece7f8";

                  return (
                    <div key={label} className="relative pr-1.5">
                      <div className="flex items-center">
                        <span
                          className="w-3.5 h-3.5 shrink-0 rounded-full border-2"
                          style={{
                            backgroundColor: dotBg,
                            borderColor: ringColor,
                          }}
                        />
                        {i < currentSteps.length - 1 && (
                          <span
                            className="flex-1 h-0.5 ml-1 rounded-full transition-all"
                            style={{ backgroundColor: lineBg }}
                          />
                        )}
                      </div>
                      <div
                        className={classNames(
                          "text-[11px] mt-2 leading-tight",
                          isCur
                            ? "font-bold text-[#241f36]"
                            : reached
                            ? "font-medium text-[#241f36]"
                            : "text-[#9b93b3]"
                        )}
                      >
                        {label}
                      </div>
                      <div className="font-mono text-[10px] text-[#9b93b3] mt-0.5">
                        {firstAtMap[i] ? firstAtMap[i].slice(0, 5) : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Sub-grid: Timeline / Events + Info Sidebar */}
            <div className="flex gap-3.5 flex-wrap items-start">
              {/* Event Timeline on the left */}
              <div className="flex-2 min-w-[340px] bg-white border border-[#ece7f8] rounded-xl overflow-hidden shadow-xs">
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#f1ecfe]">
                  <span className="text-xs font-bold text-[#241f36]">
                    Historique des jalons (Clementino Show)
                  </span>
                  <label className="flex items-center gap-1.5 text-xs text-[#6b6480] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showInternal}
                      onChange={(e) => setShowInternal(e.target.checked)}
                      className="w-3.5 h-3.5 accent-purple-700"
                    />
                    Notes internes
                  </label>
                </div>

                {/* Add event form - Clementino / change status */}
                <div className="p-4 bg-[#fbf9ff] border-b border-[#f1ecfe]">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <label className="block">
                      <span className="block text-[10px] font-bold tracking-wider text-[#8b81a8] uppercase mb-1">
                        Nouvel événement / Statut
                      </span>
                      <select
                        value={formKind}
                        onChange={(e) => setFormKind(e.target.value)}
                        className="w-full h-8 px-2.5 border border-gray-200 rounded-lg bg-white text-xs text-[#1f1b2e] focus:outline-none focus:ring-1 focus:ring-purple-400"
                      >
                        <option value="">Sélectionner une étape...</option>
                        {kindOptions.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="block text-[10px] font-bold tracking-wider text-[#8b81a8] uppercase mb-1">
                        Localisation GPS / Agence
                      </span>
                      <input
                        type="text"
                        value={formLoc}
                        onChange={(e) => setFormLoc(e.target.value)}
                        placeholder="Ex. Agence Akwa ou Hub Douala"
                        className="w-full h-8 px-2.5 border border-gray-200 rounded-lg text-xs text-[#1f1b2e] focus:outline-none focus:ring-1 focus:ring-purple-400"
                      />
                    </label>
                  </div>
                  <input
                    type="text"
                    value={formNote}
                    onChange={(e) => setFormNote(e.target.value)}
                    placeholder="Commentaire ou instructions sur cet événement"
                    className="w-full h-8 mt-2.5 px-2.5 border border-gray-200 rounded-lg text-xs text-[#1f1b2e] focus:outline-none focus:ring-1 focus:ring-purple-400"
                  />
                  <div className="flex items-center gap-4 flex-wrap mt-2.5">
                    <label className="flex items-center gap-1.5 text-xs text-[#3f3a52] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formPub}
                        onChange={(e) => setFormPub(e.target.checked)}
                        className="w-3.5 h-3.5 accent-purple-700"
                      />
                      Visible client
                    </label>
                    <label className="flex items-center gap-1.5 text-xs text-[#3f3a52] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formNotify}
                        onChange={(e) => setFormNotify(e.target.checked)}
                        className="w-3.5 h-3.5 accent-emerald-600"
                      />
                      Notifier WhatsApp
                    </label>
                    <button
                      type="button"
                      disabled={submittingEvent}
                      onClick={handleAddEvent}
                      className="ml-auto h-8 px-3.5 bg-purple-700 hover:bg-purple-800 disabled:opacity-50 rounded-lg text-xs font-bold text-white transition-colors cursor-pointer shadow-xs flex items-center gap-1.5"
                    >
                      {submittingEvent && <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />}
                      <span>Enregistrer l&apos;événement</span>
                    </button>
                  </div>
                </div>

                {/* Events list */}
                <div className="px-4 py-3 divide-y divide-[#f4f1fb]">
                  {visibleEvents.map((ev, i) => {
                    const S = SRC_STYLE[ev.src];
                    const isLatest = i === 0;
                    return (
                      <div
                        key={`${ev.at}-${i}`}
                        className="grid grid-cols-[82px_16px_minmax(0,1fr)] gap-2.5 pt-3 pb-2"
                      >
                        <div className="text-right">
                          <div className="font-mono text-[11px] font-semibold text-[#241f36]">
                            {ev.at.slice(0, 5)}
                          </div>
                          <div className="font-mono text-[10px] text-[#9b93b3]">
                            {ev.at.slice(11)}
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <span
                            className={classNames(
                              "w-2.5 h-2.5 mt-1 rounded-full",
                              isLatest ? "ring-4 ring-purple-100" : ""
                            )}
                            style={{
                              backgroundColor: isLatest ? S.dot : "#cbc4dc",
                            }}
                          />
                          {i < visibleEvents.length - 1 && (
                            <span className="flex-1 w-0.5 mt-1.5 bg-[#efeaf9]" />
                          )}
                        </div>
                        <div>
                          <div
                            className={classNames(
                              "text-xs font-semibold",
                              ev.src === "Incident" ? "text-rose-700" : "text-[#241f36]"
                            )}
                          >
                            {ev.label}
                          </div>
                          <div className="text-xs text-[#6b6480] mt-0.5 leading-relaxed">
                            {[ev.loc, ev.detail].filter(Boolean).join(" · ")}
                          </div>
                          <div className="flex gap-1.5 flex-wrap mt-1.5">
                            <span
                              className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                              style={{ backgroundColor: S.bg, color: S.fg }}
                            >
                              {ev.src}
                            </span>
                            {!ev.pub && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-600">
                                Interne
                              </span>
                            )}
                            {ev.notified && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">
                                Client notifié
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {visibleEvents.length === 0 && (
                    <div className="py-5 text-center text-xs text-[#9b93b3]">
                      Aucun événement de suivi enregistré pour cette expédition.
                    </div>
                  )}
                </div>
              </div>

              {/* Sidebar with Meta / Contacts */}
              <div className="flex-1 min-w-[240px] flex flex-col gap-3.5">
                {/* Client Link Card */}
                <div className="bg-white border border-[#ece7f8] rounded-xl p-3.5 shadow-xs">
                  <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
                    Lien de suivi client
                  </div>
                  <div className="mt-2 p-2 bg-purple-50 rounded-lg font-mono text-xs text-purple-900 break-all select-all border border-purple-100">
                    suivi.mopocrm.com/t/{currentShipment.code}
                  </div>
                  <div className="flex gap-2 mt-2.5">
                    <button
                      type="button"
                      onClick={handleCopyLink}
                      className="flex-1 h-8 border border-purple-200 bg-white hover:bg-purple-50 rounded-lg text-xs font-semibold text-purple-900 transition-colors cursor-pointer"
                    >
                      Copier
                    </button>
                    <button
                      type="button"
                      onClick={handleSendLink}
                      className="flex-1 h-8 border border-emerald-300 bg-emerald-100 hover:bg-emerald-200 rounded-lg text-xs font-bold text-emerald-800 transition-colors cursor-pointer"
                    >
                      Envoyer WhatsApp
                    </button>
                  </div>
                </div>

                {/* Sender & Receiver Card */}
                <div className="bg-white border border-[#ece7f8] rounded-xl p-3.5 shadow-xs flex flex-col gap-3">
                  <div>
                    <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
                      Expéditeur
                    </div>
                    <div className="text-xs font-bold text-[#241f36] mt-1">
                      {currentShipment.sender}
                    </div>
                    <div className="font-mono text-xs text-[#6b6480] mt-0.5">
                      {currentShipment.phone}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
                      Destinataire
                    </div>
                    <div className="text-xs font-bold text-[#241f36] mt-1">
                      {currentShipment.receiver}
                    </div>
                    <div className="font-mono text-xs text-[#6b6480] mt-0.5">
                      {currentShipment.receiverPhone}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 pt-2.5 border-t border-[#f1ecfe]">
                    <div>
                      <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
                        Colis
                      </div>
                      <div className="text-xs font-semibold text-[#241f36] mt-0.5">
                        {currentShipment.colis} colis · {currentShipment.poids}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
                        Paiement
                      </div>
                      <span
                        className="inline-block mt-0.5 px-2 py-0.5 rounded text-[11px] font-bold"
                        style={{
                          backgroundColor: PAY[currentShipment.pay].bg,
                          color: PAY[currentShipment.pay].fg,
                        }}
                      >
                        {currentShipment.pay}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
                        Nature
                      </div>
                      <div className="text-xs text-[#241f36] mt-0.5">
                        {currentShipment.nature}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-white border border-[#ece7f8] rounded-xl p-8 text-xs text-gray-400">
            {loading ? "Chargement des détails..." : "Sélectionnez une expédition pour afficher son suivi détaillé."}
          </div>
        )}
      </div>

      {/* Edit Shipment Modal */}
      {editingShipment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto tracking-scrollbar p-6">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Modifier le statut de l&apos;expédition
                </h3>
                <p className="text-xs text-gray-500 font-mono mt-0.5">
                  {editingShipment.code}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditingShipment(null)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveEdit} className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-gray-700 mb-1">Nouveau statut</label>
                <select
                  value={editFormData.status}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, status: e.target.value as Shipment["status"] })
                  }
                  className="w-full h-8 px-2.5 border border-gray-300 rounded-lg text-xs bg-white"
                >
                  {STATUSES.map((st) => (
                    <option key={st} value={st}>
                      {st}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-1">
                  Commentaire / Note d&apos;étape
                </label>
                <input
                  type="text"
                  value={editFormData.alert}
                  onChange={(e) => setEditFormData({ ...editFormData, alert: e.target.value })}
                  placeholder="Ex. Colis scellé et prêt pour embarquement"
                  className="w-full h-8 px-2.5 border border-gray-300 rounded-lg text-xs"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setEditingShipment(null)}
                  className="px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 rounded-lg text-xs font-semibold text-gray-700 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-700 hover:bg-purple-800 rounded-lg text-xs font-bold text-white transition-colors flex items-center gap-1.5 shadow-xs"
                >
                  <CheckIcon className="w-4 h-4" />
                  Mettre à jour sur Clementino
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed left-1/2 bottom-6 -translate-x-1/2 z-50 px-4 py-2.5 bg-[#1f1b2e] rounded-lg text-xs font-semibold text-white shadow-xl animate-fade-in">
          {toast}
        </div>
      )}
    </div>
  );
}
